/**
 * PROSPECT FIELDS - Everything in One File
 *
 * This file contains:
 * 1. Types
 * 2. Field Definitions
 * 3. Data Transformation (API → Field Values)
 * 4. Filtering & Sorting
 *
 * Simple Flow: API Data → Transform → Filter → Render
 */

import React, { ReactNode } from 'react';
import { CircleCheck, Calendar } from '@saleshandy/icons';
import moment from 'moment-timezone';
import Input from '../../../../../../../../shared/design-system/components/input/input';
import OutcomeCell from '../../../prospect-list-table/helpers/outcome-cell';
import ProspectSequenceName from '../../../prospect-list-table/helpers/prospect-sequence-name';
import { getEmailVerificationIcon } from '../../../../../../../../shared/functions/get-email-verification-icon';
import Icon from '../../../../../../../../shared/design-system/components/atoms/icon';
import {
  OverlayPopover,
  OverlayTooltip,
} from '../../../../../../../../shared/design-system/components/overlay';
import PrimaryValueChangeDropdown from '../primary-value-change-dropdown';
import { Placement } from '../../../../../../../../shared/design-system/components/overlay-taget/enums';
import { NON_REDIRECT_DOMAINS } from '../../../../../../helpers/non-redirect-domain';
import TruncatedTextWithTooltip from '../../../../../../../../shared/components/truncated-text-with-tooltip';
import ContactStatusTag from '../../../../../../../../shared/design-system/components/atoms/contact-status-tag/contact-status-tag';
import { mapSequenceStatusToContactFilter } from '../../../../../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/contact-list-content/components/helpers/step-filter-mapper';
import { accessibleOnClick } from '../../../../../../../../shared/utils';
import { ValidTab } from '../../../../../../../../shared/utils/prospect-details-url';
import { Field } from '../../../../../../../settings/types/field';
import { getDateTimeAsPerUserTimezone } from '../../../../../../../unified-inbox/helpers/utils/get-mail-item-duration';
import {
  getTruncateLengthEmailByScreen,
  getTruncateLengthEmailByScreenWithOutHover,
} from './prospect-attributes';
// ============================================================================
// TYPES
// ============================================================================

export type ProspectProfileFieldContext = {
  prospectOutcomeSequences: Array<{
    id: number;
    title: string;
    status: string;
    categoryId: number;
  }>;
  prospectOutcomeSubsequences?: Array<{
    id: number;
    title: string;
    status: string;
    categoryId: number;
    parentSequenceId: number;
  }>;
  selectedProspectId: number;
  allOutcomes: any[];
  prospectFieldSearch: string | null;
  onProspectFieldSearch: (value: string) => void;
  onRefreshProspectData?: () => void;
  changePrimaryEmailsInProspect?: (
    contactId: number,
    primaryEmailProspectInfoId: number,
  ) => void;
  updatePrimaryNumbers?: (
    contactId: number,
    primaryPhoneProspectInfoId: number,
  ) => void;
  setActiveTab?: (tab: string) => void;
  changeTab?: (prospectId: number, tab: ValidTab) => void;
};

export type ProspectProfileField = {
  id: string;
  label?: string;
  value?: ReactNode;
  searchValue?: string;
  showCopy?: boolean;
  showPin?: boolean;
  showAdd?: boolean;
  showEdit?: boolean;
  variant?: 'default' | 'search';
  renderValue?: (context: ProspectProfileFieldContext) => ReactNode;
};

export type ProspectProfileFieldInstance = ProspectProfileField & {
  valueNode: ReactNode;
  order: number;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

const formatDateTime = (date: string | null | undefined): string => {
  if (!date) return '-';
  try {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '-';
  }
};

const getRelativeTime = (
  date: string | null | undefined,
  userTimezone?: string,
): string => {
  if (!date) return '-';

  try {
    const tz = userTimezone || moment.tz.guess();

    // Create moments DIRECTLY in the target timezone
    const now = moment.tz(tz);
    const past = moment.tz(date, tz);

    const diffInDays = Math.floor(now.diff(past, 'days', true));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return '1 day ago';
    if (diffInDays < 30) return `${diffInDays} days ago`;

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths === 1) return '1 month ago';
    if (diffInMonths < 12) return `${diffInMonths} months ago`;

    const diffInYears = Math.floor(diffInMonths / 12);
    return diffInYears === 1 ? '1 year ago' : `${diffInYears} years ago`;
  } catch {
    return '-';
  }
};

export const getAttributeValue = (
  attributes: any[] | undefined,
  fieldLabel: string,
): string | Date | null => {
  if (!attributes) return null;
  const attr = attributes.find(
    (a) => a?.fieldRef?.label?.trim() === fieldLabel.trim(),
  );
  return attr?.attributeValue ?? null;
};

/**
 * Formats a date value from API format (YYYY-MM-DD HH:mm:ss) to YYYY-MM-DD
 * Also handles dates that are already in YYYY-MM-DD format
 */
export const formatDateToYYYYMMDD = (
  date: string | Date | null | undefined,
): string | null => {
  if (!date) return null;
  try {
    const dateStr = typeof date === 'string' ? date : date.toISOString();
    // If date is in format "YYYY-MM-DD HH:mm:ss", extract just the date part
    if (dateStr.includes(' ')) {
      return dateStr.split(' ')[0];
    }
    // If date is already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // Otherwise, parse and format
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return null;
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return null;
  }
};

/**
 * Formats a date value to YYYY-MM-DD format for API payload
 * Accepts Date object, string, or null
 */
export const formatDateForApi = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return '';
  try {
    // If it's already a string in YYYY-MM-DD format, return as is
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    // If it's a string with time, extract just the date part
    if (typeof date === 'string' && date.includes(' ')) {
      return date.split(' ')[0];
    }
    // Otherwise, convert to Date and format
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

// ============================================================================
// DATA TRANSFORMATION - API Data → Field Values
// ============================================================================

/**
 * Gets field value from prospect API data
 */
export const getFieldValueFromData = (
  fieldId: string,
  prospectData: any,
  fields?: Field[],
  userTimezone?: string,
  screenWidth?: number,
  isEmailFieldHovered?: boolean,
): React.ReactNode | null => {
  if (!prospectData) return null;

  const { attributes, engagementFields, prospectContactInfo } = prospectData;
  /**
   * Safe helper to read a value from the attributes array by label.
   * - Returns null if attributes is missing or not an array
   * - Each attribute item is expected to have shape: { fieldRef: { label }, attributeValue }
   */

  // Simple mapping: fieldId → get data → format → return
  const fieldMap: Record<string, () => React.ReactNode | null> = {
    email: () => {
      const prospectEmails = attributes?.filter(
        (item) => item?.fieldRef?.label === 'Email',
      );
      if (prospectEmails.length === 0) return null;

      // Primary email ID from main prospectData
      const primaryEmailId =
        prospectData?.primaryEmailProspectContactInfoId || null;

      // Find primary email
      const primaryEmailObj = prospectEmails?.[0]?.attributeValue?.filter(
        (item) => item?.isPrimary,
      );

      const emailVerificationStatus =
        primaryEmailObj?.[0]?.emailVerificationStatus;
      const statusCheck = getEmailVerificationIcon(
        emailVerificationStatus ?? 'skip',
      );
      const getVerificationIcon = () => (
        <Icon
          identifier={statusCheck?.identifier}
          className={statusCheck?.className}
        />
      );

      // Get primary email from object
      const primaryEmail = primaryEmailObj?.[0]?.email;
      // Remaining email count
      const remainingCount =
        attributes?.reduce((count, attr) => {
          if (attr?.fieldRef?.label === 'Email') {
            return count + attr?.attributeValue?.length;
          }
          return count;
        }, 0) - 1;
      // Transform emails for dropdown options with labels
      const emailOptions = prospectEmails?.[0]?.attributeValue?.map(
        (email) => ({
          key: email.id,
          label:
            (email.emailType || 'Other').charAt(0).toUpperCase() +
            (email.emailType || 'Other').slice(1),
          value: email.email,
          emailVerificationStatus: email.emailVerificationStatus,
          isEmail: true,
        }),
      );

      // Split local + domain
      const [local, domain] = primaryEmail.split('@');
      const [domainName, domainExt] = domain.split('.');
      const isRestrictedDomain = NON_REDIRECT_DOMAINS?.includes(domain);

      const trimLengthScreen = getTruncateLengthEmailByScreen(screenWidth);
      const trimLengthWithoutHover = getTruncateLengthEmailByScreenWithOutHover(
        screenWidth,
      );

      const trimmedLocal = (
        <TruncatedTextWithTooltip
          string={local}
          length={
            // eslint-disable-next-line no-nested-ternary
            remainingCount > 0
              ? 6
              : isEmailFieldHovered
              ? trimLengthScreen
              : trimLengthWithoutHover
          }
        />
      );
      const trimmedDomainName = (
        <TruncatedTextWithTooltip
          string={domainName}
          length={
            // eslint-disable-next-line no-nested-ternary
            remainingCount > 0
              ? 6
              : isEmailFieldHovered
              ? trimLengthScreen
              : trimLengthWithoutHover
          }
        />
      );

      return (
        <>
          <OverlayTooltip text={statusCheck?.toolTipText}>
            {getVerificationIcon()}
          </OverlayTooltip>
          <span>
            {trimmedLocal}@
            {isRestrictedDomain ? (
              <span>
                {trimmedDomainName}.{domainExt}
              </span>
            ) : (
              <a
                href={`https://${domain}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>
                  {trimmedDomainName}.{domainExt}
                </span>
              </a>
            )}
          </span>
          {remainingCount > 0 && (
            <OverlayPopover
              className="primary-value-change-dropdown-popover"
              content={
                <PrimaryValueChangeDropdown
                  options={emailOptions}
                  tooltipText="Set as Primary Email"
                  contactId={prospectData?.id}
                  initialSelectedValue={primaryEmailId}
                  modalTitle="Changing the primary email of prospect?"
                  modalContent={
                    <div className="primary-value-change-dropdown-modal-content">
                      <div className="primary-value-change-dropdown-modal-content-text">
                        The system will send all upcoming emails to the new
                        primary email address.
                      </div>
                      <div className="primary-value-change-dropdown-modal-content-text mt-3">
                        It might affect the previous email thread. Are you sure
                        you want to update it?
                      </div>
                    </div>
                  }
                />
              }
              placement={Placement.Bottom}
              rootClose
            >
              <span className="remaining-count" tabIndex={0} role="button">
                +{remainingCount} more
              </span>
            </OverlayPopover>
          )}
        </>
      );
    },
    phone: () => {
      // Extract all phone contactInfo items
      const phoneItems =
        prospectContactInfo?.filter((item) => item?.type === 'phone') || [];

      // If no phone numbers found
      if (phoneItems.length === 0) {
        return <span className="gray-txt-12">-</span>;
      }

      // Primary phone ID from main prospect data
      const primaryPhoneId = prospectData?.primaryPhoneProspectContactInfoId;

      // Find primary phone object
      const primaryPhoneObj = phoneItems.find(
        (item) => item.id === primaryPhoneId,
      );

      // Fallback to first phone if no primary exists
      const primaryPhone =
        primaryPhoneObj?.contactInfo || phoneItems[0]?.contactInfo;

      // Count remaining
      const remainingCount = phoneItems.length - 1;

      // Build dropdown options (excluding primary phone)
      const phoneOptions = phoneItems.map((item) => ({
        key: item.id,
        label:
          (item.contactInfoCategory || 'Other').charAt(0).toUpperCase() +
          (item.contactInfoCategory || 'Other').slice(1),
        value: item.contactInfo,
        isEmail: false,
      }));

      return (
        <span>
          {primaryPhone || '-'}
          {remainingCount > 0 && (
            <OverlayPopover
              className="primary-value-change-dropdown-popover"
              content={
                <PrimaryValueChangeDropdown
                  options={phoneOptions}
                  tooltipText="Set as Primary Number"
                  contactId={prospectData?.id}
                  initialSelectedValue={primaryPhoneId}
                  modalTitle="Changing the default phone number?"
                  modalContent={
                    <div className="primary-value-change-dropdown-modal-content">
                      <div className="primary-value-change-dropdown-modal-content-text">
                        The system uses merge tag of default phone number.
                      </div>
                      <div className="primary-value-change-dropdown-modal-content-text mt-3">
                        Updating it will change the phone number value for this
                        prospect.
                      </div>
                    </div>
                  }
                />
              }
              placement={Placement.Bottom}
              rootClose
            >
              <span className="remaining-count" tabIndex={0} role="button">
                +{remainingCount} more
              </span>
            </OverlayPopover>
          )}
        </span>
      );
    },
    'latest-status': () => {
      const status = mapSequenceStatusToContactFilter(
        prospectData?.finalStatus,
      );
      return status ? (
        <div className="prospect-status-tooltip">
          <ContactStatusTag status={status} isSequenceProspectTag={true} />
        </div>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'company-website': () => {
      // Prefer value from attributes (label: "Company Website")
      const website =
        getAttributeValue(attributes, 'Company Website') ||
        prospectData.companyWebsite;

      return website ? (
        <span>
          <a href={String(website)}>{String(website)}</a>
        </span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'deal-value': () => {
      const value = getAttributeValue(attributes, 'Deal Value');
      return value !== '' ? <span>{value}</span> : null;
    },
    'closing-date': () => {
      const date = getAttributeValue(attributes, 'Closing Date');
      if (date !== '' && date !== null) {
        const formattedDate = formatDateToYYYYMMDD(date);
        return formattedDate ? (
          <span>
            <Calendar /> {formattedDate}
          </span>
        ) : (
          <span className="gray-txt-12">not</span>
        );
      }
      return <span className="gray-txt-12">not</span>;
    },
    'last-contacted': () => {
      const date = engagementFields?.lastContacted;
      if (!date) {
        return <span className="gray-txt-12">-</span>;
      }
      const relativeTime = getRelativeTime(date, userTimezone);
      if (!userTimezone) {
        return <span>{relativeTime}</span>;
      }
      const dateInUserTimezone = moment.tz(date, userTimezone);
      const fullDate = `${dateInUserTimezone.format(
        'MMM DD',
      )}, ${dateInUserTimezone.format('YYYY')}, at ${dateInUserTimezone.format(
        'hh:mm A',
      )} (GMT${dateInUserTimezone.format('Z')}) ${userTimezone}`;
      return (
        <OverlayTooltip text={fullDate} className="tooltip-center">
          <span className="cursor-pointer">{relativeTime}</span>
        </OverlayTooltip>
      );
    },
    'email-verification-status': () => {
      const status = prospectData.verificationStatus;
      if (!status) return null;
      const statusCheck = getEmailVerificationIcon(status);
      const getVerificationIcon = () => (
        <Icon
          identifier={statusCheck?.identifier}
          className={statusCheck?.className}
        />
      );
      return (
        <>
          {getVerificationIcon()}
          <span>{statusCheck?.toolTipText}</span>
        </>
      );
    },
    'job-title': () => {
      const jobTitle = getAttributeValue(attributes, 'Job Title');
      return jobTitle ? (
        <span>
          <TruncatedTextWithTooltip string={String(jobTitle)} length={37} />
        </span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    department: () => {
      const dept = getAttributeValue(attributes, 'Department');
      return dept ? (
        <span>{String(dept)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    company: () => {
      const company = getAttributeValue(attributes, 'Company');
      return company ? (
        <span>{String(company)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'company-industry': () => {
      const industry = getAttributeValue(attributes, 'Company Industry');
      return industry ? (
        <span>{String(industry)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'prospect-owner': () => {
      const user = prospectData?.prospectOwner;
      return user ? (
        <span>{`${user.firstName} ${user.lastName}`}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'number-of-opens': () => {
      const opens = engagementFields?.numberOfOpens;
      return opens !== null && opens !== undefined ? (
        <span>{String(opens)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'number-of-clicks': () => {
      const clicks = engagementFields?.numberOfClicks;
      return clicks !== null && clicks !== undefined ? (
        <span>{String(clicks)}</span>
      ) : null;
    },
    'creation-date': () => {
      const date = prospectData.createdAt;
      if (!date) {
        return <span className="gray-txt-12">-</span>;
      }
      const relativeTime = getRelativeTime(date, userTimezone);
      if (!userTimezone) {
        return <span>{relativeTime}</span>;
      }
      const { fullDate } = getDateTimeAsPerUserTimezone(date, userTimezone);
      return (
        <OverlayTooltip text={fullDate} className="tooltip-center">
          <span className="cursor-pointer">{relativeTime}</span>
        </OverlayTooltip>
      );
    },
    'last-updated': () => {
      const date = prospectData.lastUpdatedAt;
      if (!date) {
        return <span className="gray-txt-12">-</span>;
      }
      const relativeTime = getRelativeTime(date, userTimezone);
      if (!userTimezone) {
        return <span>{relativeTime}</span>;
      }
      const { fullDate } = getDateTimeAsPerUserTimezone(date, userTimezone);
      return (
        <OverlayTooltip text={fullDate} className="tooltip-center">
          <span className="cursor-pointer">{relativeTime}</span>
        </OverlayTooltip>
      );
    },
    'email-verification-sub-status': () => {
      const subStatus = prospectData.verificationSubStatus;
      return subStatus ? (
        <span>{String(subStatus)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'free-email-account': () => {
      const isFree = prospectData.isFreeEmail;
      return isFree !== null && isFree !== undefined ? (
        <span>{isFree ? 'Yes' : 'No'}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'mx-record': () => {
      const { mxRecord } = prospectData;
      return mxRecord ? (
        <span>{String(mxRecord)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'creation-source': () => {
      const source = getAttributeValue(attributes, 'Creation Source');
      if (!source) return null;
      return <span>{source}</span>;
    },
    'number-of-email-sent': () => {
      const sent = engagementFields?.numberOfEmailsSent;
      return sent !== null && sent !== undefined ? (
        <span>{String(sent)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    experience: () => {
      const exp = getAttributeValue(attributes, 'Experience');
      return exp ? (
        <span>{String(exp)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    linkedin: () => {
      const linkedin = getAttributeValue(attributes, 'LinkedIn');
      return linkedin ? (
        <span>
          <a href={String(linkedin)}>{linkedin}</a>
        </span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    twitter: () => {
      const twitter = getAttributeValue(attributes, 'Twitter');
      return twitter ? (
        <span>
          <a href={String(twitter)}>{twitter}</a>
        </span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    facebook: () => {
      const facebook = getAttributeValue(attributes, 'Facebook');
      return facebook ? (
        <span>
          <a href={String(facebook)}>{facebook}</a>
        </span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    city: () => {
      const city = getAttributeValue(attributes, 'City');
      return city ? (
        <span>{String(city)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    state: () => {
      const state = getAttributeValue(attributes, 'State');
      return state ? (
        <span>{String(state)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    country: () => {
      const country = getAttributeValue(attributes, 'Country');
      return country ? (
        <span>{String(country)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'company-domain': () => {
      const domain = getAttributeValue(attributes, 'Company Domain');
      const isRestrictedDomain = NON_REDIRECT_DOMAINS?.includes(String(domain));
      if (!domain) {
        return <span className="gray-txt-12">-</span>;
      }

      return isRestrictedDomain ? (
        <span>{domain}</span>
      ) : (
        <span>
          <a
            href={`https://${domain}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {domain}
          </a>
        </span>
      );
    },
    'company-size': () => {
      const size = getAttributeValue(attributes, 'Company Size');
      return size ? (
        <span>{String(size)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'company-revenue': () => {
      const revenue = getAttributeValue(attributes, 'Company Revenue');
      return revenue ? (
        <span>{String(revenue)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'company-founded-year': () => {
      const year = getAttributeValue(attributes, 'Company Founded Year');
      return year ? (
        <span>{String(year)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'recently-open-date': () => {
      const date = engagementFields?.recentlyOpenDate;
      return date ? (
        <span>{formatDateTime(date)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'recently-clicked-date': () => {
      const date = engagementFields?.recentlyClickDate;
      return date ? (
        <span>{formatDateTime(date)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'recently-replied-date': () => {
      const date = engagementFields?.recentlyReplyDate;
      return date ? (
        <span>{formatDateTime(date)}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
    'prospect-overview': () => {
      const overview = getAttributeValue(attributes, 'Prospect Overview');
      return overview ? (
        <span>{overview}</span>
      ) : (
        <span className="gray-txt-12">-</span>
      );
    },
  };

  // Normalize field ID to handle variations (e.g., 'phone-number' -> 'phone', 'linkedIn' -> 'linkedin')
  const normalizeFieldId = (id: string): string => {
    if (id === 'phone-number') return 'phone';
    if (id === 'linkedIn') return 'linkedin';
    return id;
  };

  const normalizedFieldId = normalizeFieldId(fieldId);
  const getter = fieldMap[normalizedFieldId] || fieldMap[fieldId];

  // If it's a static field, use the getter
  if (getter) {
    return getter();
  }

  // Otherwise, treat it as a custom field from the API
  // Try to find the field by ID (fieldId could be the API field ID as string)
  const customField = fields?.find((f) => String(f.id) === String(fieldId));

  if (customField && customField.label) {
    // Use the field label from API to get the value
    const value = getAttributeValue(attributes, customField.label);
    // Show value if it exists, otherwise show hyphen (matching pattern from static fields)
    if (value) {
      // If it's a date field, format it from YYYY-MM-DD HH:mm:ss to YYYY-MM-DD
      if (customField.fieldType === 'date') {
        const formattedDate = formatDateToYYYYMMDD(value);
        return formattedDate ? (
          <span>{formattedDate}</span>
        ) : (
          <span className="gray-txt-12">-</span>
        );
      }
      return <span>{String(value)}</span>;
    }
    return <span className="gray-txt-12">-</span>;
  }

  // If custom field not found, return null (field doesn't exist)
  return null;
};

/**
 * Generates search value for a field
 */
const extractTextFromNode = (node: React.ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (React.isValidElement(node) && node.props.children) {
    return extractTextFromNode(node.props.children);
  }
  if (Array.isArray(node)) {
    return node.map(extractTextFromNode).join(' ');
  }
  return '';
};

export const getFieldSearchValue = (
  fieldId: string,
  prospectData: any,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  context?: ProspectProfileFieldContext,
): string => {
  if (!prospectData) return '';
  const value = getFieldValueFromData(fieldId, prospectData);
  if (!value) return '';
  const text = extractTextFromNode(value);
  return `${fieldId} ${text}`.toLowerCase();
};

// ============================================================================
// FIELD DEFINITIONS
// ============================================================================

/**
 * Configurable list of field IDs that should have edit functionality.
 * To add a new editable field, simply add its ID to this array.
 */
export const EDITABLE_FIELD_IDS: string[] = [
  'deal-value',
  'closing-date',
  'prospect-overview',
  'job-title',
  'department',
  'industry',
  'experience',
  'linkedIn',
  'twitter',
  'facebook',
  'website',
  'company',
  'company-website',
  'company-size',
  'company-industry',
  'company-revenue',
  'company-founded-year',
  'company-linkedIn',
  'company-phone',
  'city',
  'state',
  'country',
];

export const DEFAULT_PROSPECT_PROFILE_FIELDS: ProspectProfileField[] = [
  {
    id: 'email',
    label: 'Email',
    value: (
      <>
        <CircleCheck
          width={16}
          height={16}
          className="circle-check-green-icon"
        />
        <span>
          alexc@<a href="www.google.com">siemens.com</a>
        </span>
      </>
    ),
    searchValue: 'alexc siemens email alexc@siemens.com google',
    showAdd: true,
    showCopy: true,
    showPin: true,
  },
  {
    id: 'phone-number',
    label: 'Phone Number',
    value: <span>+91 8367765997</span>,
    searchValue: '+91 8367765997 phone',
    showAdd: true,
    showCopy: true,
    showPin: true,
  },
  {
    id: 'latest-status',
    label: 'Latest Status',
    value: <span className="status-tag completed">Active</span>,
    searchValue: 'active latest status',
    showPin: true,
  },
  {
    id: 'company-website',
    label: 'Company Website',
    value: (
      <span>
        <a href="www.siemens.com/home">www.siemens.com/home</a>
      </span>
    ),
    searchValue: 'www.siemens.com/home company website',
    showEdit: EDITABLE_FIELD_IDS?.includes('company-website'),
    showPin: true,
  },
  {
    id: 'prospect-outcome',
    label: 'Prospect Outcomes',
    renderValue: ({
      prospectOutcomeSequences,
      selectedProspectId,
      allOutcomes,
    }) =>
      prospectOutcomeSequences?.length ? (
        <OutcomeCell
          sequences={[...prospectOutcomeSequences]}
          prospectId={selectedProspectId}
          allOutcomes={allOutcomes}
        />
      ) : (
        <span className="gray-txt-12">-</span>
      ),
    searchValue: 'prospect outcome',
    showPin: true,
  },
  {
    id: 'sequence-associated',
    label: 'Sequence Associated in',
    renderValue: ({
      prospectOutcomeSequences,
      allOutcomes,
      selectedProspectId,
      setActiveTab,
      changeTab,
    }) => {
      if (prospectOutcomeSequences?.length) {
        return (
          <ProspectSequenceName
            sequences={[...prospectOutcomeSequences]}
            allOutcomes={allOutcomes}
            isSubsequence={false}
            selectedProspectId={selectedProspectId}
          />
        );
      }

      // Show clickable "Click to add" that opens sequences tab
      const handleClickToAdd = () => {
        setActiveTab?.('sequences');
        if (changeTab && selectedProspectId) {
          changeTab(selectedProspectId, 'sequences');
        }
      };

      return (
        <span
          className="gray-txt-11 cursor-pointer editable-field-click-to-add"
          {...accessibleOnClick(handleClickToAdd)}
        >
          Click to add
        </span>
      );
    },
    searchValue: 'sequence associated',
    showPin: true,
  },
  {
    id: 'deal-value',
    label: 'Deal Value',
    value: <span>1000</span>,
    showCopy: true,
    showPin: true,
    showEdit: EDITABLE_FIELD_IDS?.includes('deal-value'),
  },
  {
    id: 'closing-date',
    label: 'Closing Date',
    value: (
      <span>
        <Calendar /> Oct 8 ,2025
      </span>
    ),
    showCopy: true,
    showPin: true,
    showEdit: EDITABLE_FIELD_IDS?.includes('closing-date'),
  },
  {
    id: 'last-contacted',
    label: 'Last Contacted',
    searchValue: 'last contacted',
    showPin: true,
  },
  {
    id: 'prospect-search',
    variant: 'search',
    renderValue: ({ prospectFieldSearch, onProspectFieldSearch }) => (
      <Input
        placeholder="Search Prospect Fields"
        icons={[{ place: Input.IconPlace.Left, identifier: 'search' }]}
        value={prospectFieldSearch}
        onChange={(value) => onProspectFieldSearch(value)}
        className="prospect-profile-section-field-input"
      />
    ),
    showPin: true,
  },
  {
    id: 'email-verification-status',
    label: 'Email Verification Status',
    value: (
      <>
        <CircleCheck width={16} height={16} />
        <span>Unverified</span>
      </>
    ),
    searchValue: 'email verification status unvatified',
    showPin: true,
  },
  {
    id: 'job-title',
    label: 'Job Title',
    value: <span>Industrial Designer</span>,
    searchValue: 'job title industrial designer',
    showPin: true,
    showEdit: EDITABLE_FIELD_IDS?.includes('job-title'),
  },
  {
    id: 'department',
    label: 'Department',
    value: <span>Click to Add</span>,
    searchValue: 'department',
    showPin: true,
    showCopy: true,
    showEdit: EDITABLE_FIELD_IDS?.includes('department'),
  },
  {
    id: 'company',
    label: 'Company',
    value: <span>Siemens</span>,
    searchValue: 'company siemens',
    showPin: true,
    showEdit: EDITABLE_FIELD_IDS?.includes('company'),
  },
  {
    id: 'company-industry',
    label: 'Company Industry',
    value: <span>Manufacturing</span>,
    searchValue: 'company industry manufacturing',
    showPin: true,
    showEdit: EDITABLE_FIELD_IDS?.includes('company-industry'),
  },
  {
    id: 'prospect-owner',
    label: 'Prospect Owner',
    value: <span>Durgesh Tekale</span>,
    searchValue: 'prospect owner durgesh tekale',
    showPin: true,
  },
  {
    id: 'number-of-opens',
    label: 'Number of Opens',
    value: <span>3</span>,
    searchValue: 'number of opens 3',
    showPin: true,
  },
  {
    id: 'number-of-clicks',
    label: 'Number of Clicks',
    value: <span>0</span>,
    searchValue: 'number of clicks 0',
    showPin: true,
  },
  {
    id: 'subsequence',
    label: 'Subsequence',
    renderValue: ({
      prospectOutcomeSubsequences,
      allOutcomes,
      selectedProspectId,
      setActiveTab,
      changeTab,
    }) => {
      if (prospectOutcomeSubsequences?.length) {
        return (
          <ProspectSequenceName
            sequences={[...prospectOutcomeSubsequences]}
            allOutcomes={allOutcomes}
            isSubsequence={true}
            selectedProspectId={selectedProspectId}
          />
        );
      }

      // Show clickable "Click to add" that opens sequences tab
      const handleClickToAdd = () => {
        setActiveTab?.('sequences');
        if (changeTab && selectedProspectId) {
          changeTab(selectedProspectId, 'sequences');
        }
      };

      return (
        <span
          className="gray-txt-11 cursor-pointer editable-field-click-to-add"
          {...accessibleOnClick(handleClickToAdd)}
        >
          Click to add
        </span>
      );
    },
    searchValue: 'subsequence',
    showPin: true,
  },
  {
    id: 'creation-date',
    label: 'Creation date',
    value: <span>2 Months ago</span>,
    searchValue: 'creation date 2 months ago',
    showPin: true,
  },
  {
    id: 'last-updated',
    label: 'Last Updated',
    value: <span>2 Months ago</span>,
    searchValue: 'last updated 2 months ago',
    showPin: true,
  },
  {
    id: 'email-verification-sub-status',
    label: 'Email verification Sub status',
    value: <span>-</span>,
    searchValue: 'email verification sub status',
    showPin: true,
  },
  {
    id: 'free-email-account',
    label: 'Free Email Account',
    value: <span>-</span>,
    searchValue: 'free email account',
    showPin: true,
  },
  {
    id: 'mx-record',
    label: 'MX Record',
    value: <span>-</span>,
    searchValue: 'mx record',
    showPin: true,
  },
  {
    id: 'creation-source',
    label: 'Creation Source',
    value: <span>CSV Import</span>,
    searchValue: 'creation source csv import',
    showPin: true,
  },
  {
    id: 'number-of-email-sent',
    label: 'Number of Email Sent',
    value: <span>2</span>,
    searchValue: 'number of email sent 2',
    showPin: true,
  },
  {
    id: 'experience',
    label: 'Experience',
    value: <span>4 years</span>,
    searchValue: 'experience 4 years',
    showEdit: EDITABLE_FIELD_IDS?.includes('experience'),
    showPin: true,
  },
  {
    id: 'linkedIn',
    label: 'LinkedIn',
    value: (
      <span>
        <a href="https://in.linkedin.com/alexcarrie/profile">
          https://in.linkedin.com/alexcarrie/profile iamh...
        </a>
      </span>
    ),
    searchValue: 'linkedin https://in.linkedin.com/alexcarrie/profile',
    showEdit: EDITABLE_FIELD_IDS?.includes('linkedIn'),
    showPin: true,
  },
  {
    id: 'twitter',
    label: 'Twitter',
    value: (
      <span>
        <a href="https://x.com/">https://x.com/</a>
      </span>
    ),
    showEdit: EDITABLE_FIELD_IDS?.includes('twitter'),
    searchValue: 'twitter https://x.com',
    showPin: true,
  },
  {
    id: 'facebook',
    label: 'Facebook',
    value: (
      <span>
        <a href="www.Facebook/">www.Facebook/</a>
      </span>
    ),
    showEdit: EDITABLE_FIELD_IDS?.includes('facebook'),
    searchValue: 'facebook www.facebook.com',
    showPin: true,
  },
  {
    id: 'city',
    label: 'City',
    value: <span>Hamburg</span>,
    searchValue: 'city hamburg',
    showPin: true,
    showEdit: EDITABLE_FIELD_IDS?.includes('city'),
  },
  {
    id: 'state',
    label: 'State',
    value: <span>Click to add</span>,
    searchValue: 'state click to add',
    showPin: true,
    showEdit: EDITABLE_FIELD_IDS?.includes('state'),
  },
  {
    id: 'country',
    label: 'Country',
    value: <span>Germany</span>,
    searchValue: 'country germany',
    showPin: true,
    showEdit: EDITABLE_FIELD_IDS?.includes('country'),
  },
  {
    id: 'company-domain',
    label: 'Company Domain',
    value: (
      <span>
        <a href="siemens.com">siemens.com</a>
      </span>
    ),
    searchValue: 'company domain siemens.com',
    showPin: true,
  },
  {
    id: 'company-size',
    label: 'Company Size',
    value: <span>2000-4000</span>,
    searchValue: 'company size 2000-4000',
    showEdit: EDITABLE_FIELD_IDS?.includes('company-size'),
    showPin: true,
  },
  {
    id: 'company-revenue',
    label: 'Company Revenue',
    value: <span>120,00,90,000</span>,
    showEdit: EDITABLE_FIELD_IDS?.includes('company-revenue'),
    searchValue: 'company revenue 1200090000',
    showPin: true,
  },
  {
    id: 'company-founded-year',
    label: 'Company Founded year',
    value: <span>1946</span>,
    showEdit: EDITABLE_FIELD_IDS?.includes('company-founded-year'),
    searchValue: 'company founded year 1946',
    showPin: true,
  },
  {
    id: 'recently-open-date',
    label: 'Recently Open Date',
    value: <span>2025-09-30 07:13:00</span>,
    searchValue: 'recently open date 2025-09-30 07:13:00',
    showPin: true,
  },
  {
    id: 'recently-clicked-date',
    label: 'Recently Clicked Date',
    value: <span>2025-09-30 07:13:00</span>,
    searchValue: 'recently clicked date 2025-09-30 07:13:00',
    showPin: true,
  },
  {
    id: 'recently-replied-date',
    label: 'Recently Replied Date',
    value: <span>2025-09-30 07:13:00</span>,
    searchValue: 'recently replied date 2025-09-30 07:13:00',
    showPin: true,
  },
  {
    id: 'prospect-overview',
    label: 'Prospect Overview',
    value: <span>Alex is an industrial designer who ...</span>,
    searchValue: 'prospect overview alex industrial designer',
    showPin: true,
    showEdit: EDITABLE_FIELD_IDS?.includes('prospect-overview'),
    showCopy: true,
  },
];

// ============================================================================
// CUSTOM FIELDS CONVERSION
// ============================================================================

/**
 * List of custom field names/labels to exclude from being displayed
 * Add field names here (case-insensitive matching) to hide them from custom fields
 */
export const EXCLUDED_CUSTOM_FIELD_NAMES: string[] = [
  'first name',
  'last name',
  'email',
  'phone',
  'company',
  'title',
  'department',
  'location',
  'industry',
  'website',
  'linkedin',
  'twitter',
  'facebook',
  'Phone Number',
];

/**
 * Converts custom fields from Field[] to ProspectProfileField[]
 * These are fields that come from the API and are not in DEFAULT_PROSPECT_PROFILE_FIELDS
 */
export const convertCustomFieldsToProspectFields = (
  customFields: Field[],
  staticFieldIds: string[],
): ProspectProfileField[] => {
  if (!customFields || customFields.length === 0) {
    return [];
  }

  // Get static field labels for better matching
  const staticFieldLabels = DEFAULT_PROSPECT_PROFILE_FIELDS.map(
    (f) => f.label?.toLowerCase() || '',
  );

  // Normalize excluded field names to lowercase for case-insensitive matching
  const excludedFieldNamesLower = EXCLUDED_CUSTOM_FIELD_NAMES?.map((name) =>
    name?.toLowerCase(),
  );

  return customFields
    ?.filter((field) => {
      // Filter out fields that are already in static fields
      // Check by label (case-insensitive) against static field labels
      const fieldLabelLower = field.label?.toLowerCase() || '';

      // Check if this field label matches any static field label
      const matchesStaticLabel = staticFieldLabels.some(
        (staticLabel) => staticLabel === fieldLabelLower,
      );

      // Also check if field ID matches any static field ID pattern
      const matchesStaticId = staticFieldIds.some((staticId) => {
        const staticIdLower = staticId.toLowerCase();
        return (
          fieldLabelLower === staticIdLower ||
          fieldLabelLower === staticIdLower.replace(/-/g, ' ') ||
          fieldLabelLower === staticIdLower.replace(/-/g, '')
        );
      });

      // Check if field is in the exclusion list
      const isExcluded = excludedFieldNamesLower?.some(
        (excludedName) => fieldLabelLower === excludedName,
      );

      // Only include if it doesn't match any static field and is not excluded
      return !matchesStaticLabel && !matchesStaticId && !isExcluded;
    })
    .map((field) => {
      // Use the actual field ID from the API as the prospect field ID
      const fieldId = String(field.id);

      return {
        id: fieldId,
        label: field.label || 'Unnamed Field',
        value: null, // Will be populated dynamically
        searchValue: field.label?.toLowerCase() || '',
        showCopy: true,
        showPin: true,
        showEdit: true, // Allow editing custom fields
        // Store the original field for reference
        customFieldData: field,
      } as ProspectProfileField & { customFieldData: Field };
    });
};

// ============================================================================
// FILTERING & SORTING
// ============================================================================

const sortFields = (
  fields: ProspectProfileFieldInstance[],
  pinnedFieldIds: string[],
): ProspectProfileFieldInstance[] => {
  const getRank = (item: ProspectProfileFieldInstance, pinnedIdx: number) => {
    if (pinnedIdx !== -1) return 0; // pinned first
    if (item.variant === 'search') return 1; // search in the middle
    return 2; // everything else after search
  };

  return fields?.sort((a, b) => {
    const aPinnedIndex = pinnedFieldIds?.findIndex(
      (id) =>
        id?.toLowerCase() === a.id?.toLowerCase() ||
        id?.toLowerCase() === a.label?.toLowerCase(),
    );

    const bPinnedIndex = pinnedFieldIds?.findIndex(
      (id) =>
        id?.toLowerCase() === b.id?.toLowerCase() ||
        id?.toLowerCase() === b.label?.toLowerCase(),
    );
    const rankA = getRank(a, aPinnedIndex);
    const rankB = getRank(b, bPinnedIndex);

    if (rankA !== rankB) return rankA - rankB;

    // If both are pinned, keep their relative pinned order
    if (rankA === 0) {
      return aPinnedIndex - bPinnedIndex;
    }

    // Otherwise preserve original order
    return a.order - b.order;
  });
};

export const filterProspectProfileFields = (
  prospectProfileFields: ProspectProfileFieldInstance[],
  prospectFieldSearch: string | null,
  pinnedFieldIds: string[],
): ProspectProfileFieldInstance[] => {
  const normalizedSearch = prospectFieldSearch?.toLowerCase()?.trim() || '';
  const hasSearchTerm = Boolean(normalizedSearch);

  const filtered = prospectProfileFields?.filter((field) => {
    if (field.variant === 'search') return true;

    // Always include pinned fields regardless of search term
    // Check both id and label (case-insensitive) to match sorting logic
    const isPinned = pinnedFieldIds?.some(
      (id) =>
        id?.toLowerCase() === field?.id?.toLowerCase() ||
        id?.toLowerCase() === field?.label?.toLowerCase(),
    );
    if (isPinned) return true;

    if (!hasSearchTerm) return true;

    const labelValue = field?.label?.toLowerCase() || '';
    const searchableValue = field?.searchValue?.toLowerCase() || '';
    return (
      labelValue?.includes(normalizedSearch) ||
      searchableValue?.includes(normalizedSearch)
    );
  });

  return sortFields(filtered, pinnedFieldIds);
};
